import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { ContractId, ResponseOf } from '@shared/contracts';
import { ApiError, callApi, type CallOptions } from '@/lib/apiClient';

/**
 * The single `createApi`.
 *
 * ONE, never several: cache tags do not cross a `createApi` boundary, so splitting it
 * silently breaks cross-module invalidation — creating an employee would leave the
 * department counts stale with nothing to point at. Features inject their endpoints
 * here (ADR-010).
 *
 * The base query is the contract client, not `fetch`. Everything that makes a request
 * safe — the CSRF header, the single-flight 401 refresh, the development-time response
 * check against the contract schema — lives in `callApi`, and a second request path
 * would quietly bypass all of it.
 */

/**
 * A DISTRIBUTED union over the registry, not `{ id: ContractId; options?: ... }`.
 *
 * The non-distributed form computes the options across every contract at once, and the
 * body and query types collapse to their intersection — `undefined` — so no endpoint
 * can be called with a body at all. The mapped type keeps each id paired with its own
 * options.
 */
export type ContractArgs = {
  [K in ContractId]: { id: K; options?: CallOptions<K> };
}[ContractId];

/**
 * A plain object, not the `ApiError` instance.
 *
 * RTK keeps the error in the store, and Redux's serializability check — rightly —
 * rejects class instances there.
 */
export interface QueryError {
  code: string;
  message: string;
  status: number;
  details?: { path: string; message: string }[];
}

const contractBaseQuery: BaseQueryFn<ContractArgs, unknown, QueryError> = async (args) => {
  try {
    // The pairing of `id` with `options` is guaranteed by the union above; TypeScript
    // cannot carry that correlation through a single call, so it is asserted once here
    // rather than at every call site.
    const data = await callApi(args.id, args.options as never);
    return { data };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        error: {
          code: err.code,
          message: err.message,
          status: err.status,
          ...(err.details ? { details: err.details } : {}),
        },
      };
    }
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the server. Check your connection and try again.',
        status: 0,
      },
    };
  }
};

/**
 * The whole tag vocabulary, in one union.
 *
 * Declared centrally so a feature cannot invent a tag that nothing provides — an
 * invalidation that matches nothing fails silently, and the symptom appears later as a
 * screen that will not refresh.
 */
export const TAG_TYPES = [
  'Employee',
  'EmployeeList',
  'EmployeeHistory',
  'Department',
  'Hierarchy',
  'MyTeam',
  'Me',
  'Notification',
  'UnreadCount',
  'NotificationPreferences',
  'AttendanceDay',
  'AttendanceToday',
  'AttendanceCorrection',
  'Approval',
  'Leave',
  'LeaveBalance',
  'LeaveType',
  'Task',
  'TaskComment',
  'WorkReport',
  'Document',
  'DocumentChecklist',
  'Onboarding',
  'Holiday',
  'Announcement',
  'Acknowledgement',
  'NotificationPolicy',
  'DeliveryLog',
  'Dashboard',
  'WorkSession',
  'CompanySettings',
  'AuditLog',
  'PortalActivity',
  'SupportTicket',
  'SupportNotificationSettings',
] as const;

export type TagType = (typeof TAG_TYPES)[number];

export const api = createApi({
  reducerPath: 'api',
  baseQuery: contractBaseQuery,
  tagTypes: TAG_TYPES,
  // A directory left open in a tab goes stale; re-focusing refetches it rather than
  // showing yesterday's reporting lines.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});

/** Field errors keyed by name, ready for `react-hook-form`'s `setError`. */
export function fieldErrorsOf(error: QueryError | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const detail of error?.details ?? []) {
    out[detail.path.replace(/^(body|query|params)\./, '')] = detail.message;
  }
  return out;
}

/** Narrow an unknown RTK error to ours. */
export function asQueryError(error: unknown): QueryError | undefined {
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    return error as QueryError;
  }
  return undefined;
}

/** Typed helper so a feature's endpoint declaration stays one line of real content. */
export type Result<K extends ContractId> = ResponseOf<K>;
